import { useCallback, useEffect, useMemo, useState } from "react";
import { requireSupabase } from "@/lib/supabase";
import type { PlatformOrgRow, PlatformProfileRow, PlatformViewTab } from "./platformPanelTypes";

function isPlatformRpcPermissionError(error: unknown): boolean {
  const err = error as { code?: string; message?: string; details?: string; hint?: string } | null;
  const raw = `${err?.code ?? ""} ${err?.message ?? ""} ${err?.details ?? ""} ${err?.hint ?? ""}`.toLowerCase();
  return raw.includes("p0001") || (raw.includes("permission") && raw.includes("platform"));
}

export function usePlatformPanel(opts?: { enabled?: boolean }) {
  const sb = useMemo(() => requireSupabase(), []);
  const enabled = opts?.enabled !== false;

  const [tab, setTab] = useState<PlatformViewTab>("overview");
  const [msg, setMsg] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const [orgs, setOrgs] = useState<PlatformOrgRow[]>([]);
  const [pendingProfiles, setPendingProfiles] = useState<PlatformProfileRow[]>([]);
  const [blockedProfiles, setBlockedProfiles] = useState<PlatformProfileRow[]>([]);

  const [query, setQuery] = useState<string>("");
  const [searchResults, setSearchResults] = useState<PlatformProfileRow[]>([]);
  const [lastRpcError, setLastRpcError] = useState<string | null>(null);
  const [rpcPermissionBlocked, setRpcPermissionBlocked] = useState(false);

  const [manageOrgId, setManageOrgId] = useState<string | null>(null);
  const [manageOrgMemberCount, setManageOrgMemberCount] = useState<number | null>(null);
  const [manageCountLoading, setManageCountLoading] = useState(false);
  const [transferToOrgId, setTransferToOrgId] = useState<string>("");

  const orgById = useMemo(() => {
    const m = new Map<string, PlatformOrgRow>();
    for (const o of orgs) m.set(o.id, o);
    return m;
  }, [orgs]);

  const refreshOverview = useCallback(async () => {
    if (!enabled || rpcPermissionBlocked) {
      setOrgs([]);
      setPendingProfiles([]);
      setBlockedProfiles([]);
      setLastRpcError(null);
      setMsg("");
      return;
    }
    setMsg("");
    setLastRpcError(null);
    setLoading(true);
    try {
      const [orgsRpc, pendingRpc, blockedRpc] = await Promise.all([
        sb.rpc("platform_list_orgs"),
        sb.rpc("platform_list_profiles", { p_access_status: "pending", p_limit: 50 }),
        sb.rpc("platform_list_profiles", { p_access_status: "blocked", p_limit: 50 }),
      ]);

      if (orgsRpc.error) throw orgsRpc.error;
      if (pendingRpc.error) throw pendingRpc.error;
      if (blockedRpc.error) throw blockedRpc.error;

      setOrgs((orgsRpc.data as PlatformOrgRow[]) ?? []);
      setPendingProfiles((pendingRpc.data as PlatformProfileRow[]) ?? []);
      setBlockedProfiles((blockedRpc.data as PlatformProfileRow[]) ?? []);
    } catch (e: unknown) {
      if (isPlatformRpcPermissionError(e)) {
        setRpcPermissionBlocked(true);
        setOrgs([]);
        setPendingProfiles([]);
        setBlockedProfiles([]);
        setLastRpcError(null);
        setMsg("");
        return;
      }
      const err = e as { message?: string };
      const m = err?.message || String(e);
      setLastRpcError(m);
      setMsg(m);
    } finally {
      setLoading(false);
    }
  }, [enabled, rpcPermissionBlocked, sb]);

  useEffect(() => {
    if (!enabled || rpcPermissionBlocked) return;
    void refreshOverview();
  }, [enabled, refreshOverview, rpcPermissionBlocked]);

  async function doApproveAssign(profileId: string, orgId: string, role: string) {
    const org = orgById.get(orgId);
    const orgName = org?.name ?? orgId;
    const ok = window.confirm(`Make these changes for "${orgName}" organization?`);
    if (!ok) return;

    setMsg("");
    setLoading(true);
    try {
      const { error } = await sb.rpc("admin_approve_profile", {
        p_profile_id: profileId,
        p_org_id: orgId,
        p_role: role,
      });
      if (error) throw error;
      setMsg("Approved + role assigned.");
      await refreshOverview();
    } catch (e: unknown) {
      const err = e as { message?: string };
      setMsg(err?.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  async function doBlock(profileId: string, orgId: string, reason: string) {
    const org = orgById.get(orgId);
    const orgName = org?.name ?? orgId;
    const ok = window.confirm(`Make these changes for "${orgName}" organization?`);
    if (!ok) return;

    setMsg("");
    setLoading(true);
    try {
      const { error } = await sb.rpc("admin_block_profile", {
        p_profile_id: profileId,
        p_org_id: orgId,
        p_reason: reason,
        p_deactivate_memberships: true,
      });
      if (error) throw error;
      setMsg("User blocked.");
      await refreshOverview();
    } catch (e: unknown) {
      const err = e as { message?: string };
      setMsg(err?.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  async function openManageOrg(orgId: string) {
    setManageOrgId(orgId);
    setManageOrgMemberCount(null);
    setTransferToOrgId("");
    setManageCountLoading(true);
    try {
      const { data, error } = await sb.rpc("platform_get_org_member_count", { p_org_id: orgId });
      if (error) throw error;
      setManageOrgMemberCount(data as number);
    } catch (e: unknown) {
      const err = e as { message?: string };
      setManageOrgMemberCount(0);
      setMsg(err?.message || String(e));
    } finally {
      setManageCountLoading(false);
    }
  }

  async function doDeactivateOrg(org: PlatformOrgRow) {
    const memberCount = manageOrgMemberCount ?? 0;
    const transferOrg = transferToOrgId ? orgById.get(transferToOrgId) : undefined;

    let confirmMsg: string;
    if (transferOrg) {
      confirmMsg = `Transfer ${memberCount} member(s) from "${org.name}" to "${transferOrg.name}" and deactivate "${org.name}"?\n\nThis cannot be undone.`;
    } else if (memberCount > 0) {
      confirmMsg = `WARNING: "${org.name}" has ${memberCount} active member(s) and no transfer target is selected.\n\nThose members will lose their org assignment. Proceed anyway?`;
    } else {
      confirmMsg = `Deactivate "${org.name}"?\n\nThis cannot be undone.`;
    }

    if (!window.confirm(confirmMsg)) return;

    setLoading(true);
    setMsg("");
    try {
      const { error } = await sb.rpc("platform_deactivate_org", {
        p_org_id: org.id,
        p_transfer_to_org_id: transferToOrgId || null,
      });
      if (error) throw error;
      setMsg(`"${org.name}" deactivated.${transferOrg ? ` Members transferred to "${transferOrg.name}".` : ""}`);
      setManageOrgId(null);
      await refreshOverview();
    } catch (e: unknown) {
      const err = e as { message?: string };
      setMsg(err?.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  async function runSearch() {
    const q = query.trim();
    if (!q) return;
    setMsg("");
    setLastRpcError(null);
    setLoading(true);
    try {
      const { data, error } = await sb.rpc("platform_search_profiles", { p_query: q, p_limit: 25 });
      if (error) throw error;
      setSearchResults(((data as unknown[]) ?? []) as PlatformProfileRow[]);
    } catch (e: unknown) {
      const err = e as { message?: string };
      const m = err?.message || String(e);
      setLastRpcError(m);
      setMsg(m);
    } finally {
      setLoading(false);
    }
  }

  return {
    tab,
    setTab,
    msg,
    loading,
    orgs,
    pendingProfiles,
    blockedProfiles,
    query,
    setQuery,
    searchResults,
    lastRpcError,
    manageOrgId,
    setManageOrgId,
    manageOrgMemberCount,
    manageCountLoading,
    transferToOrgId,
    setTransferToOrgId,
    orgById,
    refreshOverview,
    doApproveAssign,
    doBlock,
    openManageOrg,
    doDeactivateOrg,
    runSearch,
  };
}
